import React, { useEffect, useState } from "react";
import { api, formatError, money } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Pencil, Save, X } from "lucide-react";
import PhotoUpload from "@/components/PhotoUpload";

const Field = ({ label, value, onChange, type = "text", textarea = false }) => {
  if (type === "photo") {
    return <PhotoUpload label={label} value={value || ""} onChange={onChange} testid={`field-photo-${(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />;
  }
  if (type === "course") {
    return <CourseSelect label={label} value={value || ""} onChange={onChange} />;
  }
  const isArray = type === "array";
  const display = isArray ? (Array.isArray(value) ? value.join(", ") : (value || "")) : value;
  const handle = (raw) => {
    if (isArray) onChange(raw.split(",").map((s) => s.trim()).filter(Boolean));
    else if (type === "number") onChange(Number(raw));
    else onChange(raw);
  };
  return (
    <label className="block"><div className="overline mb-1">{label}</div>
      {textarea ? (
        <textarea rows={3} value={display ?? ""} onChange={(e) => handle(e.target.value)} className="w-full rounded-lg bg-ink-900/[0.04] border border-ink-900/10 px-3 py-2 outline-none focus:border-neon text-sm" />
      ) : (
        <input type={isArray ? "text" : type} value={display ?? ""} onChange={(e) => handle(e.target.value)} className="w-full rounded-lg bg-ink-900/[0.04] border border-ink-900/10 px-3 py-2 outline-none focus:border-neon text-sm" />
      )}
    </label>
  );
};

const CourseSelect = ({ label, value, onChange }) => {
  const [courses, setCourses] = useState([]);
  useEffect(() => {
    api.get("/admin/courses").then(({ data }) => setCourses(data || [])).catch(() => setCourses([]));
  }, []);
  return (
    <label className="block">
      <div className="overline mb-1">{label}</div>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg bg-ink-900/[0.04] border border-ink-900/10 px-3 py-2 outline-none focus:border-neon text-sm"
        data-testid={`course-select-${(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      >
        <option value="">— Select a course —</option>
        {courses.map((c) => (
          <option key={c.course_id} value={c.course_id}>{c.title}</option>
        ))}
      </select>
      {value && !courses.find((c) => c.course_id === value) && (
        <div className="text-xs text-red-500 mt-1">Current value "{value}" is not a valid course — please pick from the list.</div>
      )}
    </label>
  );
};

export function AdminCourses() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/courses").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      if (edit.course_id) await api.put(`/admin/courses/${edit.course_id}`, edit);
      else await api.post("/admin/courses", edit);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(formatError(e)); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/courses/${id}`); load(); };
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="font-display text-3xl font-bold tracking-tight">Courses</h1>
        <button onClick={() => setEdit({ title: "", short_description: "", description: "", thumbnail: "", original_price: 0, current_price: 0, level: "Beginner", duration: "", category: "AI Automation", lessons_count: 0, modules_count: 0, status: "published", instructor: "Niraj Bhaardwaj" })} className="btn-neon !py-2 text-sm" data-testid="admin-add-course"><Plus className="w-4 h-4 mr-1" /> New course</button>
      </div>
      <div className="glass rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-900/[0.04]"><tr className="text-left text-ink-600"><th className="p-3">Title</th><th>Price</th><th>Status</th><th></th></tr></thead>
          <tbody>{items.map((c) => (
            <tr key={c.course_id} className="border-t border-ink-900/[0.06]" data-testid={`admin-course-${c.slug}`}>
              <td className="p-3 font-medium">{c.title} <div className="text-xs text-ink-500">/{c.slug}</div></td>
              <td>{money(c.current_price)} <span className="text-ink-500 line-through text-xs">{money(c.original_price)}</span></td>
              <td><span className="text-xs uppercase text-gold">{c.status}</span></td>
              <td className="flex gap-2 p-3"><button onClick={() => setEdit(c)}><Pencil className="w-4 h-4" /></button><button onClick={() => del(c.course_id)}><Trash2 className="w-4 h-4 text-red-400" /></button></td>
            </tr>))}
          </tbody>
        </table>
      </div>
      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-ink-900/40" onClick={() => setEdit(null)}>
          <div onClick={(e) => e.stopPropagation()} className="glass-strong rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-auto space-y-3">
            <div className="flex justify-between items-center"><h3 className="font-display text-xl text-ink-900">{edit.course_id ? "Edit" : "New"} course</h3><button onClick={() => setEdit(null)}><X /></button></div>
            <Field label="Title" value={edit.title} onChange={(v) => setEdit({ ...edit, title: v })} />
            <Field label="Short description" value={edit.short_description} onChange={(v) => setEdit({ ...edit, short_description: v })} textarea />
            <Field label="Long description" value={edit.description} onChange={(v) => setEdit({ ...edit, description: v })} textarea />
            <PhotoUpload label="Course thumbnail" value={edit.thumbnail} onChange={(v) => setEdit({ ...edit, thumbnail: v })} testid={`course-thumb-${edit.course_id || "new"}`} />
            <PhotoUpload label="Course banner (optional)" value={edit.banner} onChange={(v) => setEdit({ ...edit, banner: v })} testid={`course-banner-${edit.course_id || "new"}`} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Original price" type="number" value={edit.original_price} onChange={(v) => setEdit({ ...edit, original_price: v })} />
              <Field label="Current price" type="number" value={edit.current_price} onChange={(v) => setEdit({ ...edit, current_price: v })} />
              <Field label="Level" value={edit.level} onChange={(v) => setEdit({ ...edit, level: v })} />
              <Field label="Duration" value={edit.duration} onChange={(v) => setEdit({ ...edit, duration: v })} />
              <Field label="Category" value={edit.category} onChange={(v) => setEdit({ ...edit, category: v })} />
              <Field label="Instructor" value={edit.instructor} onChange={(v) => setEdit({ ...edit, instructor: v })} />
              <Field label="Lessons" type="number" value={edit.lessons_count} onChange={(v) => setEdit({ ...edit, lessons_count: v })} />
              <Field label="Modules" type="number" value={edit.modules_count} onChange={(v) => setEdit({ ...edit, modules_count: v })} />
              <Field label="Status" value={edit.status} onChange={(v) => setEdit({ ...edit, status: v })} />
            </div>
            <button onClick={save} className="btn-neon w-full" data-testid="admin-course-save"><Save className="w-4 h-4 mr-1" /> Save</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminOrders() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/orders").then(({ data }) => setItems(data)); }, []);
  return (
    <div className="space-y-6">
      <h1 className="font-display text-3xl font-bold tracking-tight">Orders</h1>
      <div className="glass rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-ink-900/[0.04]"><tr className="text-left text-ink-600"><th className="p-3">Order</th><th>Customer</th><th>Phone</th><th>Course</th><th>Amount</th><th>Status</th><th>Date</th></tr></thead>
          <tbody>{items.map((o) => (
            <tr key={o.order_id} className="border-t border-ink-900/[0.06]" data-testid={`order-row-${o.order_id}`}>
              <td className="p-3 font-mono text-xs">{o.order_id}</td>
              <td><div>{o.customer_name}</div><div className="text-xs text-ink-500">{o.customer_email}</div></td>
              <td className="text-ink-700 whitespace-nowrap" data-testid={`order-phone-${o.order_id}`}>{o.customer_phone ? <a href={`tel:${o.customer_phone.replace(/\s+/g,"")}`} className="hover:text-gold">{o.customer_phone}</a> : <span className="text-ink-400">—</span>}</td>
              <td>{o.course_title}</td>
              <td>{money(o.final_price)}</td>
              <td><span className="text-xs uppercase text-gold">{o.payment_status}</span></td>
              <td className="text-ink-500 text-xs">{new Date(o.created_at).toLocaleDateString()}</td>
            </tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminStudents() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/students").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const toggle = async (u) => { await api.post(`/admin/students/${u.user_id}/toggle`); load(); };
  return (
    <div className="space-y-6">
      <h1 className="font-display text-3xl font-bold tracking-tight">Students</h1>
      <div className="glass rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-ink-900/[0.04]"><tr className="text-left text-ink-600"><th className="p-3">Name</th><th>Email</th><th>Phone</th><th>Purchases</th><th>Spent</th><th>Status</th><th></th></tr></thead>
          <tbody>{items.map((u) => (
            <tr key={u.user_id} className="border-t border-ink-900/[0.06]" data-testid={`student-row-${u.user_id}`}>
              <td className="p-3">{u.name}</td>
              <td className="text-ink-600">{u.email}</td>
              <td className="text-ink-700 whitespace-nowrap" data-testid={`student-phone-${u.user_id}`}>{u.phone ? <a href={`tel:${u.phone.replace(/\s+/g,"")}`} className="hover:text-gold">{u.phone}</a> : <span className="text-ink-400">—</span>}</td>
              <td>{u.purchases_count}</td>
              <td>{money(u.total_spent)}</td>
              <td><span className={u.disabled ? "text-red-400 text-xs" : "text-gold text-xs"}>{u.disabled ? "DISABLED" : "ACTIVE"}</span></td>
              <td><button onClick={() => toggle(u)} className="btn-ghost !py-1 !px-3 text-xs">{u.disabled ? "Enable" : "Disable"}</button></td>
            </tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CrudBase({ title, endpoint, columns, empty, editorFields, idKey, testPrefix }) {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get(`/admin/${endpoint}`).then(({ data }) => setItems(data));
  useEffect(() => { load(); }, [endpoint]);
  const save = async () => {
    try {
      if (edit[idKey]) await api.put(`/admin/${endpoint}/${edit[idKey]}`, edit);
      else await api.post(`/admin/${endpoint}`, edit);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(formatError(e)); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/admin/${endpoint}/${id}`); load(); };
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="font-display text-3xl font-bold tracking-tight">{title}</h1>
        <button onClick={() => setEdit({ ...empty })} className="btn-neon !py-2 text-sm" data-testid={`admin-add-${testPrefix}`}><Plus className="w-4 h-4 mr-1" /> New</button>
      </div>
      <div className="glass rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-ink-900/[0.04]"><tr className="text-left text-ink-600">{columns.map((c) => <th key={c.k} className="p-3">{c.l}</th>)}<th></th></tr></thead>
          <tbody>{items.map((it) => (
            <tr key={it[idKey]} className="border-t border-ink-900/[0.06]" data-testid={`${testPrefix}-row-${it[idKey]}`}>
              {columns.map((c) => <td key={c.k} className="p-3">{c.render ? c.render(it) : String(it[c.k] ?? "")}</td>)}
              <td className="p-3 flex gap-2"><button onClick={() => setEdit(it)}><Pencil className="w-4 h-4" /></button><button onClick={() => del(it[idKey])}><Trash2 className="w-4 h-4 text-red-400" /></button></td>
            </tr>))}
          </tbody>
        </table>
      </div>
      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-ink-900/40" onClick={() => setEdit(null)}>
          <div onClick={(e) => e.stopPropagation()} className="glass-strong rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-auto space-y-3">
            <div className="flex justify-between items-center"><h3 className="font-display text-xl">{edit[idKey] ? "Edit" : "New"}</h3><button onClick={() => setEdit(null)}><X /></button></div>
            {editorFields.map((f) => (
              <Field key={f.k} label={f.l} type={f.type} textarea={f.textarea} value={edit[f.k]} onChange={(v) => setEdit({ ...edit, [f.k]: v })} />
            ))}
            <button onClick={save} className="btn-neon w-full"><Save className="w-4 h-4 mr-1" /> Save</button>
          </div>
        </div>
      )}
    </div>
  );
}

export const AdminServices = () => (
  <CrudBase title="Services" endpoint="services" idKey="service_id" testPrefix="service"
    empty={{ title: "", short_description: "", description: "", thumbnail: "", price: 0, original_price: 0, currency: "INR", billing_period: "one_time", price_note: "starting from", duration: "", features: [], cta_label: "Book a consultation", cta_link: "/contact", is_active: true, order: 0 }}
    columns={[
      {k:"title",l:"Title"},
      {k:"price",l:"Price",render:(s)=>s.price?money(s.price):"On request"},
      {k:"original_price",l:"Original",render:(s)=>s.original_price?money(s.original_price):"—"},
      {k:"billing_period",l:"Billing"},
      {k:"is_active",l:"Active",render:(s)=>s.is_active?"Yes":"No"}
    ]}
    editorFields={[
      {k:"title",l:"Service title"},
      {k:"short_description",l:"Short description",textarea:true},
      {k:"description",l:"Long description",textarea:true},
      {k:"thumbnail",l:"Service photo",type:"photo"},
      {k:"original_price",l:"Original price (strike-through, 0 = none)",type:"number"},
      {k:"price",l:"Current price (0 = 'On request')",type:"number"},
      {k:"currency",l:"Currency (INR, USD, EUR, …)"},
      {k:"billing_period",l:"Billing period (one_time | monthly | yearly | quote)"},
      {k:"price_note",l:"Price note (e.g., 'starting from', 'per project')"},
      {k:"duration",l:"Duration label (e.g. '4-week engagement')"},
      {k:"features",l:"Features (comma-separated)",type:"array"},
      {k:"cta_label",l:"CTA label"},
      {k:"cta_link",l:"CTA link (e.g. /contact)"},
      {k:"order",l:"Display order",type:"number"},
    ]} />
);

export const AdminOffers = () => (
  <CrudBase title="Offers" endpoint="offers" idKey="offer_id" testPrefix="offer"
    empty={{ course_id: "", name: "", description: "", discount_percentage: 0, discounted_price: 0, starts_at: "", ends_at: "", coupon_code: "", is_active: true }}
    columns={[{k:"name",l:"Name"},{k:"course_id",l:"Course"},{k:"discounted_price",l:"Price",render:(o)=>money(o.discounted_price)},{k:"is_active",l:"Active",render:(o)=>o.is_active?"Yes":"No"}]}
    editorFields={[
      {k:"name",l:"Offer name"},{k:"course_id",l:"Course",type:"course"},{k:"description",l:"Description",textarea:true},
      {k:"discount_percentage",l:"Discount %",type:"number"},{k:"discounted_price",l:"Discounted price",type:"number"},
      {k:"starts_at",l:"Starts at (ISO)"},{k:"ends_at",l:"Ends at (ISO)"},{k:"coupon_code",l:"Coupon code"}
    ]} />
);
export const AdminCoupons = () => (
  <CrudBase title="Coupons" endpoint="coupons" idKey="coupon_id" testPrefix="coupon"
    empty={{ code: "", discount_type: "percentage", value: 0, max_uses: 0, used_count: 0, min_purchase: 0, applicable_courses: [], expires_at: "", is_active: true }}
    columns={[{k:"code",l:"Code"},{k:"discount_type",l:"Type"},{k:"value",l:"Value"},{k:"used_count",l:"Used"},{k:"is_active",l:"Active",render:(c)=>c.is_active?"Yes":"No"}]}
    editorFields={[
      {k:"code",l:"Code"},{k:"discount_type",l:"Type (percentage|fixed)"},{k:"value",l:"Value",type:"number"},
      {k:"max_uses",l:"Max uses",type:"number"},{k:"min_purchase",l:"Min purchase",type:"number"},{k:"expires_at",l:"Expires at (ISO)"}
    ]} />
);
export const AdminTestimonials = () => (
  <CrudBase title="Testimonials" endpoint="testimonials" idKey="testimonial_id" testPrefix="testimonial"
    empty={{ name: "", role: "", profile_image: "", testimonial: "", rating: 5, course_id: "", approved: true }}
    columns={[{k:"name",l:"Name"},{k:"role",l:"Role"},{k:"rating",l:"★"},{k:"approved",l:"Approved",render:(t)=>t.approved?"Yes":"No"}]}
    editorFields={[{k:"name",l:"Name"},{k:"role",l:"Role"},{k:"profile_image",l:"Profile image URL"},{k:"testimonial",l:"Testimonial",textarea:true},{k:"rating",l:"Rating",type:"number"},{k:"course_id",l:"Course (optional)",type:"course"}]} />
);
export const AdminFAQs = () => (
  <CrudBase title="FAQs" endpoint="faqs" idKey="faq_id" testPrefix="faq"
    empty={{ question: "", answer: "", order: 0, course_id: "" }}
    columns={[{k:"question",l:"Question"},{k:"order",l:"Order"}]}
    editorFields={[{k:"question",l:"Question"},{k:"answer",l:"Answer",textarea:true},{k:"order",l:"Order",type:"number"},{k:"course_id",l:"Course (optional)",type:"course"}]} />
);

export function AdminMessages() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/messages").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);
  const del = async (id) => { await api.delete(`/admin/messages/${id}`); load(); };
  return (
    <div className="space-y-6">
      <h1 className="font-display text-3xl font-bold tracking-tight">Messages</h1>
      <div className="space-y-3">{items.map((m) => (
        <div key={m.message_id} className="glass rounded-xl p-5 flex justify-between items-start gap-6" data-testid={`msg-${m.message_id}`}>
          <div>
            <div className="flex gap-3 items-center"><div className="font-medium">{m.name}</div><div className="text-xs text-ink-500">{m.email} • {m.phone}</div></div>
            <div className="text-ink-600 text-sm mt-1">{m.subject}</div>
            <p className="text-ink-800 mt-2 text-sm whitespace-pre-line">{m.message}</p>
            <div className="text-xs text-ink-500 mt-2">{new Date(m.created_at).toLocaleString()}</div>
          </div>
          <button onClick={() => del(m.message_id)}><Trash2 className="w-4 h-4 text-red-400" /></button>
        </div>))}
      </div>
    </div>
  );
}

export function AdminSiteContent() {
  const [s, setS] = useState(null);
  const galleryFileRef = React.useRef(null);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  useEffect(() => { api.get("/site-settings").then(({ data }) => setS(data || {})); }, []);
  if (!s) return null;
  const gallery = Array.isArray(s.gallery) ? s.gallery : [];
  const save = async () => {
    try {
      const payload = { ...s };
      delete payload._id; delete payload.key; delete payload.updated_at; delete payload.created_at;
      await api.put("/admin/site-settings", payload); toast.success("Saved");
    } catch (e) { toast.error(formatError(e)); }
  };
  const uploadGallery = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingGallery(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ url: data.url, caption: "" });
      }
      setS({ ...s, gallery: [...gallery, ...uploaded] });
      toast.success(`${uploaded.length} photo${uploaded.length > 1 ? "s" : ""} uploaded`);
    } catch (e) { toast.error(formatError(e)); } finally { setUploadingGallery(false); }
  };
  const removeGalleryItem = (idx) => {
    const next = gallery.slice(); next.splice(idx, 1);
    setS({ ...s, gallery: next });
  };
  const updateCaption = (idx, caption) => {
    const next = gallery.slice(); next[idx] = { ...next[idx], caption };
    setS({ ...s, gallery: next });
  };
  const resolveSrc = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="font-display text-3xl font-bold tracking-tight text-ink-900">Site content</h1>
      <PhotoUpload value={s.niraj_photo_url} onChange={(v) => setS({ ...s, niraj_photo_url: v })} label="Niraj's photo (Home & About)" testid="niraj-photo" />
      <PhotoUpload value={s.hero_background_url} onChange={(v) => setS({ ...s, hero_background_url: v })} label="Hero background wallpaper (Home page)" testid="hero-bg" />

      <div className="glass rounded-2xl p-6 space-y-4" data-testid="gallery-editor">
        <div className="flex items-center justify-between">
          <div>
            <div className="overline">About-page gallery</div>
            <p className="text-xs text-ink-500 mt-1">Add multiple headshots and behind-the-scenes photos ({gallery.length}/12).</p>
          </div>
          <input type="file" accept="image/*" multiple ref={galleryFileRef} onChange={(e) => uploadGallery(Array.from(e.target.files || []))} className="hidden" data-testid="gallery-file-input" />
          <button type="button" onClick={() => galleryFileRef.current?.click()} disabled={uploadingGallery || gallery.length >= 12}
            className="btn-neon !py-2 !px-4 text-sm disabled:opacity-50" data-testid="gallery-add-btn">
            {uploadingGallery ? "Uploading..." : "Add photos"}
          </button>
        </div>
        {gallery.length === 0 ? (
          <p className="text-sm text-ink-500">No gallery photos yet.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {gallery.map((g, i) => (
              <div key={i} className="relative group rounded-xl overflow-hidden border border-ink-900/10 bg-cream-100" data-testid={`gallery-item-${i}`}>
                <img src={resolveSrc(g.url)} alt="" className="aspect-square w-full object-cover" />
                <button type="button" onClick={() => removeGalleryItem(i)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 hover:bg-red-500 hover:text-white text-ink-700 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`gallery-remove-${i}`}>
                  <X className="w-4 h-4" />
                </button>
                <input value={g.caption || ""} onChange={(e) => updateCaption(i, e.target.value)} placeholder="Caption (optional)"
                  className="w-full bg-cream-50 border-t border-ink-900/10 px-3 py-2 text-xs outline-none focus:bg-cream-100 text-ink-900" data-testid={`gallery-caption-${i}`} />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="glass rounded-xl p-6 space-y-3">
        <Field label="Homepage headline" value={s.homepage_headline} onChange={(v) => setS({ ...s, homepage_headline: v })} textarea />
        <Field label="Homepage subheadline" value={s.homepage_subheadline} onChange={(v) => setS({ ...s, homepage_subheadline: v })} textarea />
        <Field label="About long" value={s.about_long} onChange={(v) => setS({ ...s, about_long: v })} textarea />
        <Field label="Contact email" value={s.contact_email} onChange={(v) => setS({ ...s, contact_email: v })} />
        <Field label="Contact phone" value={s.contact_phone} onChange={(v) => setS({ ...s, contact_phone: v })} />
        <Field label="Address (shown on Contact & Footer)" value={s.address} onChange={(v) => setS({ ...s, address: v })} textarea />
        <div className="overline mt-3">Announcement bar</div>
        <label className="flex items-center gap-3 text-sm text-ink-800"><input type="checkbox" checked={s.announcement_active || false} onChange={(e) => setS({ ...s, announcement_active: e.target.checked })} /> Active</label>
        <Field label="Announcement text" value={s.announcement_text} onChange={(v) => setS({ ...s, announcement_text: v })} />
        <Field label="Announcement link" value={s.announcement_link} onChange={(v) => setS({ ...s, announcement_link: v })} />
        <div className="overline mt-3">Statistics</div>
        {Object.entries(s.stats || {}).map(([k, v]) => (
          <Field key={k} label={k} value={v} onChange={(nv) => setS({ ...s, stats: { ...s.stats, [k]: nv } })} />
        ))}
        <button onClick={save} className="btn-neon w-full" data-testid="admin-site-save"><Save className="w-4 h-4 mr-1" /> Save</button>
      </div>
    </div>
  );
}

export const AdminSettings = () => (
  <div className="max-w-2xl">
    <h1 className="font-display text-3xl font-bold tracking-tight mb-6">Settings</h1>
    <div className="glass rounded-xl p-6 text-ink-600 text-sm">Settings (currency, tax, email provider, payments) are managed via <code className="text-gold">/app/backend/.env</code>. Business content is under Site Content.</div>
  </div>
);
